/**
 * Display helpers shared between the events table and the snapshot
 * lightbox. Centralised here so the two surfaces can't drift —
 * if a column's formatting changes in the table, the lightbox
 * picks it up automatically (and vice versa).
 */

/**
 * Formatter cache keyed by IANA timezone. We render thousands of rows
 * per report, so building a fresh `Intl.DateTimeFormat` per call would
 * be needlessly expensive — the constructor is one of the slower
 * paths in the Intl API.
 */
const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = FORMATTER_CACHE.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      timeZone,
    });
    FORMATTER_CACHE.set(timeZone, fmt);
  }
  return fmt;
}

/**
 * Formats an ISO timestamp the way the events table renders it,
 * e.g. "Fri 29 May, 8:56". Year is intentionally omitted — the
 * report header already establishes the reporting window so
 * repeating it on every row adds noise.
 *
 * `timeZone` is the site's IANA zone (e.g. `"Australia/Melbourne"`).
 * Required because the events table, snapshot lightbox, and
 * daily-activity chart must all agree on which weekday an event
 * falls under regardless of where the report is being read — passing
 * the viewer's local zone by accident silently breaks that contract.
 *
 * Falls back to the raw ISO string if parsing fails (matches the
 * table's behaviour, which would otherwise render "Invalid Date").
 */
export function formatEventTimestamp(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return getFormatter(timeZone).format(date);
}

export interface ActivityTone {
  bg: "muted" | "danger" | "warning" | "success";
  fg: "text" | "textReversed" | "textMuted";
}

/**
 * Maps an activity_type string to the badge's background/foreground
 * tokens. "Dumping" lands on `danger` (the report's headline signal);
 * everything else stays neutral so the danger colour retains meaning.
 */
export function activityTone(activityType: string): ActivityTone {
  const a = activityType.toLowerCase();
  if (a.includes("dump")) return { bg: "danger", fg: "textReversed" };
  if (a.includes("vehicle")) return { bg: "muted", fg: "text" };
  if (a.includes("people") || a.includes("person")) return { bg: "muted", fg: "text" };
  return { bg: "muted", fg: "textMuted" };
}
